import { Component } from '@theme/component';
import { fetchConfig, translateText, translateInto, translateSubtree } from '@theme/utilities';
import { CartUpdateEvent, CartErrorEvent } from '@theme/events';

const selectors = {
  DIALOG_BODY: '[ref="body"]',
  TEMPLATE: ':scope > template',
  TEXT_COMPONENTS: 'text-component',
  SUBMIT_BTN: '.cart-variant__submit',
  DIALOG: '[data-cart-variant-dialog]',
  CART_ITEMS_COMPONENT: 'cart-items-component',
  DIALOG_HEADING: '.cart-variant-dialog__heading',
  CART_ITEMS_COMPONENTS: 'cart-items-component[data-section-id]',
};

/**
 * Handles changing the variant of a cart line item via a shared dialog.
 *
 * @extends {Component}
 */
class CartVariant extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #onClick = async () => {
    if (this.hasAttribute('disabled')) return;

    const dialog = this.#getDialog();
    if (!dialog) return;

    const body = dialog.querySelector(selectors.DIALOG_BODY);
    if (!body) return;

    const template = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector(selectors.TEMPLATE)
    );
    if (!(template instanceof HTMLTemplateElement)) return;

    const heading = dialog.querySelector(selectors.DIALOG_HEADING);

    const clonedContent = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const formClone = /** @type {HTMLFormElement | null} */ (clonedContent.querySelector('form'));
    if (!formClone) return;

    body.innerHTML = '';
    body.appendChild(clonedContent);

    formClone.addEventListener('change', this.#onVariantChange);
    formClone.addEventListener('submit', this.#onSubmit);

    // <template> content + the source-locale heading attr both bypass Weglot —
    // translate before opening so the dialog never flashes source text.
    await Promise.all([
      translateText(this.dataset.title ?? '').then((title) => {
        if (heading) heading.textContent = title;
      }),
      translateSubtree(body),
    ]);

    /** @type {any} */ (dialog).showDialog?.();
  };

  #onVariantChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.name !== 'variant_id') return;

    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const submitBtn = /** @type {HTMLElement | null} */ (form.querySelector(selectors.SUBMIT_BTN));
    if (!submitBtn) return;

    const prefix = submitBtn.dataset.prefix ?? '';
    const variantTitle = event.target.dataset.title ?? '';
    const label = prefix ? `${prefix} ${variantTitle}` : variantTitle;

    // Rebuilt from source-locale data attributes on every change.
    translateInto(submitBtn, label);
  };

  #onSubmit = async (event) => {
    event.preventDefault();

    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const formData = new FormData(form);

    const itemKey = /** @type {string} */ (formData.get('id') ?? '');
    const newVariantId = parseInt(/** @type {string} */ (formData.get('variant_id') ?? '0'), 10);
    const currentVariantId = parseInt(this.dataset.currentVariant ?? '0', 10);
    const quantity = parseInt(this.dataset.quantity ?? '1', 10);
    if (!itemKey || !newVariantId || !quantity) return;

    const dialog = /** @type {any} */ (this.#getDialog());

    if (newVariantId === currentVariantId) {
      dialog?.closeDialog?.();
      return;
    }

    const existingQty = [
      ...document.querySelectorAll(`cart-variant[data-current-variant="${newVariantId}"]`),
    ].reduce(
      (sum, el) =>
        el !== this
          ? sum + parseInt(/** @type {HTMLElement} */ (el).dataset.quantity ?? '0', 10)
          : sum,
      0,
    );

    const properties = /** @type {Record<string, string>} */ ({});
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = String(value);
    }

    form.inert = true;
    dialog?.closeDialog?.();
    /** @type {any} */ (this.closest(selectors.CART_ITEMS_COMPONENT))?.shimmerPrices();
    this.querySelectorAll(selectors.TEXT_COMPONENTS).forEach((el) =>
      /** @type {any} */ (el).shimmer(),
    );

    try {
      const updateData = await this.#updateVariants(newVariantId, itemKey, quantity, existingQty);
      if (!updateData) return;

      let finalData = updateData;
      if (Object.keys(properties).length > 0) {
        const newItem = updateData.items?.find(
          (/** @type {any} */ item) => item.variant_id === newVariantId,
        );
        if (newItem) {
          finalData =
            (await this.#updateProperties(newItem.key, newItem.quantity, properties)) ?? updateData;
        }
      }

      document.dispatchEvent(
        new CartUpdateEvent(finalData, Object.keys(finalData.sections ?? {})[0] ?? '', {
          itemCount: finalData.item_count ?? 0,
          source: 'cart-variant',
          sections: finalData.sections ?? {},
        }),
      );
    } catch (error) {
      console.error(error);
    } finally {
      form.inert = false;
    }
  };

  /**
   * @param {number} newVariantId
   * @param {string} itemKey
   * @param {number} quantity
   * @param {number} existingQty
   * @returns {Promise<any>}
   */
  #updateVariants = async (newVariantId, itemKey, quantity, existingQty) =>
    this.#cartFetch(Theme.routes.cart_update_url, {
      updates: { [newVariantId]: quantity + existingQty, [itemKey]: 0 },
      ...this.#sectionsPayload(),
    });

  /**
   * @param {string} itemKey
   * @param {number} quantity
   * @param {Record<string, string>} properties
   * @returns {Promise<any>}
   */
  #updateProperties = async (itemKey, quantity, properties) =>
    this.#cartFetch(Theme.routes.cart_change_url, {
      id: itemKey,
      quantity,
      properties,
      ...this.#sectionsPayload(),
    });

  /**
   * @returns {Record<string, string>}
   */
  #sectionsPayload = () => {
    const sections = [
      ...new Set(
        [...document.querySelectorAll(selectors.CART_ITEMS_COMPONENTS)]
          .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
          .filter(Boolean),
      ),
    ];
    return sections.length > 0
      ? { sections: sections.join(','), sections_url: window.location.pathname }
      : {};
  };

  /**
   * @param {string} url
   * @param {Record<string, any>} payload
   * @returns {Promise<any>}
   */
  #cartFetch = async (url, payload) => {
    const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(payload) }));
    const data = await response.json();

    if (data.errors) {
      this.dispatchEvent(new CartErrorEvent('', data.errors, '', {}));
      return null;
    }

    return data;
  };

  /**
   * @returns {HTMLElement | null}
   */
  #getDialog() {
    return /** @type {HTMLElement | null} */ (document.querySelector(selectors.DIALOG));
  }

}

if (!customElements.get('cart-variant')) {
  customElements.define('cart-variant', CartVariant);
}
